import React, { useState } from 'react';
import {
  Tenancy,
  isPaymentOverdue,
  getDaysOverdue
} from '../../types/tenancyTypes';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  BellAlertIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import PaymentHistoryDropdown from './PaymentHistoryDropdown';
import TenancySubTenants from '../tenancies/TenancySubTenants';
import {
  formatCurrency,
  getMonthName,
  getOrdinalSuffix
} from '../../utility/formatters';

interface TenancyCardProps {
  tenancy: Tenancy;
  onMarkPaid: (tenancy: Tenancy) => void;
  onSubTenantsChanged?: () => void;
  onSendReminder?: (tenancy: Tenancy) => void;
}

const TenancyCard: React.FC<TenancyCardProps> = ({
  tenancy,
  onMarkPaid,
  onSubTenantsChanged,
  onSendReminder
}) => {
  const [showSubTenants, setShowSubTenants] = useState(false);
  const overdue = isPaymentOverdue(tenancy);
  const daysOverdue = getDaysOverdue(tenancy);
  const isPaid = tenancy.currentPeriod.paymentStatus === 'paid';

  // UX: Dynamic styling for the card border/shadow based on urgency
  const containerClasses = overdue
    ? 'border-l-4 border-l-red-500 border-gray-200 shadow-md ring-1 ring-red-50'
    : 'border-l-4 border-l-transparent border-gray-200 hover:border-gray-300';

  const getPaymentStatusBadge = () => {
    if (isPaid) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
          Paid
        </span>
      );
    } else if (overdue) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-pulse">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />
          Overdue ({daysOverdue} days)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <ClockIcon className="w-3.5 h-3.5 mr-1" />
          Pending
        </span>
      );
    }
  };

  return (
    <div
      className={`rounded-xl bg-white p-6 transition-all duration-200 ${containerClasses}`}
    >
      {/* --- Header Section: Tenant Info --- */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              {tenancy.tenantInfo.name}
            </h3>
            {getPaymentStatusBadge()}
          </div>

          <div className="mt-2 space-y-1">
            <div className="flex items-center text-sm text-gray-600">
              <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
              <a
                href={`mailto:${tenancy.tenantInfo.email}`}
                className="hover:text-blue-600 hover:underline"
              >
                {tenancy.tenantInfo.email}
              </a>
            </div>

            {tenancy.tenantInfo.phone && tenancy.tenantInfo.phone !== '0' && (
              <div className="flex items-center text-sm text-gray-600">
                <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                <a
                  href={`tel:${tenancy.tenantInfo.phone}`}
                  className="hover:text-blue-600 hover:underline"
                >
                  {tenancy.tenantInfo.phone}
                </a>
              </div>
            )}
          </div>

          {/* Sub-tenants Toggle (Moved closer to tenant info) */}
          {tenancy.id && (
            <button
              onClick={() => setShowSubTenants(!showSubTenants)}
              className="mt-2 flex items-center text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              <UserGroupIcon className="w-3.5 h-3.5 mr-1" />
              {showSubTenants ? 'Hide sub-tenants' : 'Manage sub-tenants'}
            </button>
          )}
        </div>

        {/* Arrears Badge (if any) */}
        {(tenancy.arrears ?? 0) > 0 && (
          <div className="text-right">
            <div className="text-xs text-gray-500 uppercase">Total Arrears</div>
            <div className="text-red-600 font-bold">
              {formatCurrency(tenancy.arrears)}
            </div>
          </div>
        )}
      </div>

      {/* --- Action Zone: Financial Info & Controls --- */}
      {/* This gray box now contains the primary action (Mark as Paid) for better context */}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 rounded-xl border ${overdue ? 'bg-red-50/50 border-red-100' : 'bg-gray-50 border-gray-100'} overflow-hidden`}
      >
        {/* Left: Rent Details */}
        <div className="p-5 border-b md:border-b-0 md:border-r border-gray-200/60">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Monthly Rent
          </p>
          <div className="flex items-baseline gap-2">
            <p className="font-bold text-2xl text-gray-900">
              {formatCurrency(tenancy.rentAmount)}
            </p>
            <span className="text-xs text-gray-500">
              due {getOrdinalSuffix(tenancy.rentDueDay)}
            </span>
          </div>
        </div>

        {/* Right: Current Period & Actions */}
        <div className="p-5 flex flex-col justify-center">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Current Period
            </p>
            {/* Status Text (if paid) */}
            {isPaid && (
              <span className="text-xs font-bold text-green-600">PAID</span>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-lg text-gray-900">
                {getMonthName(tenancy.currentPeriod.month)}{' '}
                {tenancy.currentPeriod.year}
              </p>
              {tenancy.currentPeriod.amountPaid > 0 && !isPaid && (
                <p className="text-xs text-green-600 font-medium">
                  Partially paid:{' '}
                  {formatCurrency(tenancy.currentPeriod.amountPaid)}
                </p>
              )}
            </div>

            {/* PRIMARY ACTION BUTTONS */}
            {!isPaid && (
              <div className="flex items-center gap-2">
                {overdue && onSendReminder && (
                  <button
                    onClick={() => onSendReminder(tenancy)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    title="Send Reminder"
                  >
                    <BellAlertIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => onMarkPaid(tenancy)}
                  className="whitespace-nowrap px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all transform active:scale-95"
                >
                  Mark Paid
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- Footer: Billing History --- */}
      <div className="mt-6">
        <PaymentHistoryDropdown billingHistory={tenancy.billingHistory} />
      </div>

      {/* --- Progressive Disclosure: Sub-tenants --- */}
      {tenancy.id && showSubTenants && (
        <div className="mt-4 pt-4 border-t border-gray-100 animation-fade-in">
          <TenancySubTenants
            tenancyId={tenancy.id}
            subTenants={tenancy.subTenants}
            onChanged={onSubTenantsChanged}
          />
        </div>
      )}
    </div>
  );
};

export default TenancyCard;
